import type { ReactNode } from "react";
import type { DomainCode } from "@/constants/domains";
import type { IepContent } from "@/services/repositories/assessment-repository";
import type { Assessment, AssessmentEntry, Student } from "@/types/assessment";
import type { ScaleEntry } from "@/types/scale";
import { formatDate } from "@/utils/date";
import { assessmentRecordLabel } from "@/utils/student-workspace";
import {
  PrintableReportDocument,
  PrintableReportHeader,
} from "@/components/report/PrintableReport";

export const ANALYSIS_FIELDS = [
  { key: "cognitive", label: "认知（语言/语前）" },
  { key: "expressive", label: "语言表达" },
  { key: "receptive", label: "语言理解" },
  { key: "fineMotor", label: "小肌肉" },
  { key: "grossMotor", label: "大肌肉（体能）" },
  { key: "imitation", label: "模仿" },
  { key: "emotion", label: "情感表达" },
  { key: "social", label: "社交互动" },
  { key: "selfCare", label: "自理" },
  { key: "behavior", label: "行为" },
] as const;

export const DEVELOPMENT_FIELDS = [
  { key: "feature", label: "儿童障碍及其个性特征" },
  { key: "impact", label: "其障碍对常规课程学习的影响" },
  { key: "courseNeed", label: "课程学习及特殊教育需要" },
  { key: "otherNeed", label: "其他特殊教育需要" },
] as const;

export const SUMMARY_FIELDS = [
  { key: "barrier", label: "障碍影响" },
  { key: "strengths", label: "学习优势 / 弱势" },
  { key: "resources", label: "融合教育资源" },
  { key: "potential", label: "未来发展潜力" },
  { key: "expectation", label: "家长期望" },
] as const;

export const PRIMARY_PLAN_CODES: DomainCode[] = ["HE", "LA", "SO", "SC", "AR"];
export const PLAN_DOMAINS = [
  { code: "HE", label: "目标 1：健康" },
  { code: "LA", label: "目标 2：语言" },
  { code: "SO", label: "目标 3：社会" },
  { code: "SC", label: "目标 4：科学" },
  { code: "AR", label: "目标 5：艺术" },
  { code: "OTHER", label: "目标 6：其他领域" },
] as const;

export type PlanDomainCode = (typeof PLAN_DOMAINS)[number]["code"];

export interface IepDomainEntry {
  entry: ScaleEntry;
  record: AssessmentEntry;
}

export function IepPrintableDocument({
  student,
  assessment,
  content,
  domainBuckets,
  entryIndex,
}: {
  student: Student | undefined;
  assessment: Assessment;
  content: IepContent;
  domainBuckets: Map<PlanDomainCode, IepDomainEntry[]>;
  entryIndex: Map<string, ScaleEntry>;
}): JSX.Element {
  return (
    <PrintableReportDocument ariaLabel="IEP 打印文档">
      <PrintableReportHeader
        eyebrow="个别化教育计划"
        title="个别化教育计划（IEP）"
        description="根据评估结果制定教育计划与学期目标。"
        student={student}
        assessmentLabel={`${formatDate(assessment.startedAt)} · ${assessmentRecordLabel()}`}
      />

      <PrintSection title="一、评估结果分析">
        <div className="grid grid-cols-2 gap-3">
          {ANALYSIS_FIELDS.map((field) => (
            <PrintField
              key={field.key}
              label={field.label}
              value={content.analysis[field.key] ?? ""}
            />
          ))}
        </div>
      </PrintSection>

      <PrintSection title="二、发展与课程需要">
        <div className="grid grid-cols-2 gap-3">
          {DEVELOPMENT_FIELDS.map((field) => (
            <PrintField
              key={field.key}
              label={field.label}
              value={content.development[field.key] ?? ""}
            />
          ))}
        </div>
      </PrintSection>

      <PrintSection title="三、综合摘要">
        <div className="grid grid-cols-2 gap-3">
          {SUMMARY_FIELDS.map((field) => (
            <PrintField
              key={field.key}
              label={field.label}
              value={content.summary[field.key] ?? ""}
            />
          ))}
        </div>
      </PrintSection>

      <PrintSection title="四、教育目标" newPage>
        <div className="space-y-4">
          {PLAN_DOMAINS.map((domain) => {
            const domainEntries = domainBuckets.get(domain.code) ?? [];
            const currentLevel = domainEntries.filter((item) => item.record.score === 2);
            const selectedDetails = content.educationPlan[domain.code].goalEntryCodes
              .map((code) => entryIndex.get(code))
              .filter((entry): entry is ScaleEntry => Boolean(entry));
            return (
              <section
                key={domain.code}
                className="iep-print-block rounded border border-slate-300 p-3"
              >
                <h3 className="font-semibold">{domain.label}</h3>
                <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                  <PrintList
                    label="目前水平"
                    items={currentLevel.map(
                      ({ entry }) => `${entry.code} · ${getEntryTitle(entry)}`,
                    )}
                  />
                  <PrintList
                    label="学期目标"
                    items={selectedDetails.map(
                      (entry) => `${entry.code} · ${getEntryTitle(entry)}`,
                    )}
                  />
                </div>
                <div className="mt-3">
                  <PrintList
                    label="目标基准"
                    items={selectedDetails.map(
                      (entry) => `${entry.code} · ${getEntryTitle(entry)}：${entry.standard}`,
                    )}
                  />
                </div>
                <div className="mt-3">
                  <p className="text-xs font-semibold text-slate-600">评价</p>
                  <p
                    className="mt-1 whitespace-pre-wrap break-words"
                    data-print-evaluation={domain.code}
                  >
                    {content.educationPlan[domain.code].evaluationNote || "\u00a0"}
                  </p>
                </div>
              </section>
            );
          })}
        </div>
      </PrintSection>

      <PrintSection title="五、支持服务与家庭指导" newPage>
        <h3 className="font-semibold">特殊需要支持与服务安排</h3>
        <div className="mt-3 space-y-3">
          {content.supportServices.map((row, index) => (
            <section key={index} className="iep-print-row rounded border border-slate-300 p-3">
              <h4 className="font-semibold">服务安排 {index + 1}</h4>
              <dl className="mt-2 grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                <PrintMeta label="支持项目" value={row.serviceName} />
                <PrintMeta label="负责人员 / 机构" value={row.provider} />
                <PrintMeta label="服务内容 / 形式" value={row.content} />
                <PrintMeta label="服务频率" value={row.frequency} />
                <PrintMeta label="开始日期" value={row.startDate} />
                <PrintMeta label="结束日期" value={row.endDate} />
                <PrintMeta label="备注" value={row.note} />
              </dl>
            </section>
          ))}
        </div>
        <div className="mt-5 grid grid-cols-2 gap-3">
          <PrintField label="家庭康复指导内容" value={content.familyGuidance.guidance} />
          <PrintField label="家长支援服务" value={content.familyGuidance.parentSupport} />
        </div>
      </PrintSection>
    </PrintableReportDocument>
  );
}

function PrintSection({
  title,
  children,
  newPage = false,
}: {
  title: string;
  children: ReactNode;
  newPage?: boolean;
}): JSX.Element {
  return (
    <section className={`iep-print-section pt-5 ${newPage ? "iep-print-new-page" : ""}`}>
      <h2 className="mb-4 border-b border-slate-300 pb-2 text-xl font-semibold">{title}</h2>
      {children}
    </section>
  );
}

function PrintField({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <section className="iep-print-block rounded border border-slate-300 p-3">
      <h3 className="text-xs font-semibold text-slate-600">{label}</h3>
      <p className="mt-1 whitespace-pre-wrap break-words">{value || "\u00a0"}</p>
    </section>
  );
}

function PrintList({ label, items }: { label: string; items: string[] }): JSX.Element {
  return (
    <div>
      <p className="text-xs font-semibold text-slate-600">{label}</p>
      {items.length > 0 ? (
        <ul className="mt-1 list-disc space-y-1 pl-5">
          {items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      ) : (
        <p className="mt-1">{"\u00a0"}</p>
      )}
    </div>
  );
}

function PrintMeta({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div>
      <dt className="inline font-semibold text-slate-600">{label}：</dt>
      <dd className="inline whitespace-pre-wrap break-words">{value}</dd>
    </div>
  );
}

function getEntryTitle(entry: ScaleEntry): string {
  return entry.shortTermGoal?.title ?? entry.longTermGoal.title;
}
